import React, { useState, useEffect, useCallback } from 'react';
import { 
  AlertCircle, 
  Check, 
  Eye, 
  EyeOff, 
  Save, 
  Settings, 
  ExternalLink,
  Loader2,
  Info,
  RefreshCw,
  ChevronDown,
  Users,
  Key,
  Globe,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { apiClient } from '@/lib/api';
import { toast } from 'sonner';
import { PillButton } from '@/components/ios26/IOSComponents';

/**
 * MetaAppConfig - Composant mutualisé pour la configuration Meta App (Facebook + Instagram)
 * 
 * P1 REFACTORED: Version complète avec:
 * - Configuration Meta partagée (App ID + App Secret)
 * - Sélection de pages Facebook
 * - États clairs et distincts
 * - Sécurité du secret (jamais exposé en clair)
 * - Distinction Facebook / Instagram
 * 
 * Collection backend: meta_credentials (alias de facebook_credentials pour backward compat)
 */

// ============================================================================
// TYPES & CONSTANTS
// ============================================================================

// États fonctionnels clairs
const META_STATES = {
  META_APP_NOT_CONFIGURED: 'META_APP_NOT_CONFIGURED',
  META_APP_CONFIGURED: 'META_APP_CONFIGURED',
  OAUTH_NOT_CONNECTED: 'OAUTH_NOT_CONNECTED',
  OAUTH_CONNECTED: 'OAUTH_CONNECTED',
  NO_PAGE_FOUND: 'NO_PAGE_FOUND',
  PAGE_SELECTION_REQUIRED: 'PAGE_SELECTION_REQUIRED',
  PAGE_SELECTED: 'PAGE_SELECTED',
  PAGE_TOKEN_MISSING: 'PAGE_TOKEN_MISSING',
  READY_TO_PUBLISH: 'READY_TO_PUBLISH'
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

// Status badge with icon
const StatusBadge = ({ status, label }) => {
  const configs = {
    ok: { bg: 'bg-green-500/10', text: 'text-green-600', border: 'border-green-500/20', Icon: CheckCircle2 },
    warning: { bg: 'bg-amber-500/10', text: 'text-amber-600', border: 'border-amber-500/20', Icon: AlertCircle },
    error: { bg: 'bg-red-500/10', text: 'text-red-600', border: 'border-red-500/20', Icon: XCircle },
    info: { bg: 'bg-blue-500/10', text: 'text-blue-600', border: 'border-blue-500/20', Icon: Info }
  };
  
  const config = configs[status] || configs.info;
  const { Icon } = config;
  
  return (
    <span className={cn(
      'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border',
      config.bg, config.text, config.border
    )}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
};

// Status row for grid display
const StatusRow = ({ label, value, status, icon: IconComponent }) => (
  <div className="flex items-center justify-between py-2 border-b border-semantic-border/50 last:border-b-0">
    <span className="text-sm text-semantic-muted flex items-center gap-2">
      {IconComponent && <IconComponent className="w-4 h-4" />}
      {label}
    </span>
    <StatusBadge status={status} label={value} />
  </div>
);

// Page selector dropdown
const PageSelector = ({ pages, selectedPageId, onSelect, loading, onRefresh }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const selectedPage = pages.find(p => p.id === selectedPageId);
  
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-semantic">
          Sélectionner une Page Facebook
        </label>
        <button
          onClick={onRefresh}
          disabled={loading}
          className="p-1.5 rounded-lg hover:bg-semantic-surface-3 transition-colors"
          title="Actualiser les pages"
        >
          <RefreshCw className={cn("w-4 h-4 text-semantic-muted", loading && "animate-spin")} />
        </button>
      </div>
      
      {pages.length === 0 ? (
        <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
          <p className="text-sm text-amber-700 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            Aucune Page Facebook trouvée. Vérifiez que vous êtes administrateur d'une Page.
          </p>
        </div>
      ) : (
        <div className="relative">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={cn(
              "w-full flex items-center justify-between p-3 rounded-xl border transition-all",
              "bg-semantic-surface hover:bg-semantic-surface-2",
              isOpen ? "border-blue-500" : "border-semantic-border"
            )}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Globe className="w-5 h-5 text-blue-600" />
              </div>
              <div className="text-left">
                <p className="font-medium text-semantic">
                  {selectedPage?.name || 'Sélectionner une page...'}
                </p>
                {selectedPage && (
                  <p className="text-xs text-semantic-muted">
                    {selectedPage.category} • {selectedPage.fan_count?.toLocaleString() || 0} fans
                  </p>
                )}
              </div>
            </div>
            <ChevronDown className={cn(
              "w-5 h-5 text-semantic-muted transition-transform",
              isOpen && "rotate-180"
            )} />
          </button>
          
          {isOpen && (
            <div className="absolute z-10 w-full mt-2 py-2 rounded-xl border border-semantic-border bg-semantic-surface shadow-lg max-h-64 overflow-y-auto">
              {pages.map(page => (
                <button
                  key={page.id}
                  onClick={() => {
                    onSelect(page.id);
                    setIsOpen(false);
                  }}
                  className={cn(
                    "w-full flex items-center gap-3 p-3 hover:bg-semantic-surface-2 transition-colors",
                    page.id === selectedPageId && "bg-blue-500/10"
                  )}
                >
                  <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                    <Globe className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="text-left flex-1 min-w-0">
                    <p className="font-medium text-semantic truncate">{page.name}</p>
                    <p className="text-xs text-semantic-muted">
                      {page.category} • {page.fan_count?.toLocaleString() || 0} fans
                    </p>
                  </div>
                  {page.id === selectedPageId && (
                    <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function MetaAppConfig({ 
  platform = 'facebook', // 'facebook' or 'instagram'
  projectId,
  onConfigSaved,
  onStatusChange,
  showOAuthButton = true,
  oauthButtonLabel = 'Connecter via OAuth'
}) {
  // ============================================================================
  // STATE
  // ============================================================================
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [selectingPage, setSelectingPage] = useState(false);
  const [loadingPages, setLoadingPages] = useState(false);
  
  // Meta App config state
  const [useCustomApp, setUseCustomApp] = useState(false);
  const [appId, setAppId] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [hasExistingSecret, setHasExistingSecret] = useState(false);
  
  // Connection status state
  const [metaState, setMetaState] = useState(META_STATES.META_APP_NOT_CONFIGURED);
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  
  // Pages state
  const [pages, setPages] = useState([]);
  const [selectedPageId, setSelectedPageId] = useState(null);
  const [selectedPageName, setSelectedPageName] = useState(null);
  
  // ============================================================================
  // DATA LOADING
  // ============================================================================
  
  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Load Meta App config (shared for Facebook and Instagram)
      const configResp = await apiClient.get('/social/facebook/config');
      const configData = configResp.data;
      setConfig(configData);
      
      setUseCustomApp(configData.use_custom_app || false);
      // SECURITY: Never store full App ID from server - only masked version
      if (configData.app_id_masked) {
        setAppId(configData.app_id_masked);
      }
      setHasExistingSecret(configData.has_app_secret || false);
      
      // 2. Load connection status - use platform-specific endpoint
      // P1 FIX: Instagram uses /integrations/instagram/status, Facebook uses /integrations/facebook/status
      const statusEndpoint = platform === 'instagram' 
        ? '/social/instagram/status' 
        : '/integrations/facebook/status';
      
      const statusResp = await apiClient.get(statusEndpoint);
      const statusData = statusResp.data;
      setStatus(statusData);
      
      // For Instagram, also check the specific instagram_connected flag
      if (platform === 'instagram') {
        // Instagram status has different fields
        const igConnected = statusData.instagram_connected || 
                           statusData.is_configured || 
                           statusData.has_instagram_credentials ||
                           (statusData.instagram_account_id && statusData.has_page_token);
        statusData.instagram_connected = igConnected;
        console.info('[MetaAppConfig] Instagram status:', { 
          instagram_connected: igConnected,
          instagram_account_id: statusData.instagram_account_id,
          has_page_token: statusData.has_page_token,
          chain_status: statusData.chain_status
        });
      }
      
      // Extract page info
      setSelectedPageId(statusData.selected_page_id || statusData.page_id || null);
      setSelectedPageName(statusData.selected_page_name || statusData.page_name || null);
      
      // 3. Determine current state
      const newState = computeMetaState(configData, statusData, platform);
      setMetaState(newState);
      
      // 4. Load pages if OAuth connected but no page selected (Facebook only)
      if (platform === 'facebook' && statusData.has_user_token && !statusData.selected_page_id) {
        await loadPages();
      }
      
      // 5. Notify parent
      onStatusChange?.({
        state: newState,
        configured: isMetaConfigured(configData),
        hasOAuth: statusData.has_user_token || statusData.instagram_connected,
        hasPages: (statusData.available_pages_count || 0) > 0,
        pageSelected: !!statusData.selected_page_id,
        canPublish: statusData.can_publish,
        platform
      });
      
    } catch (error) {
      console.error('[MetaAppConfig] Load error:', error);
      setMetaState(META_STATES.META_APP_NOT_CONFIGURED);
    } finally {
      setLoading(false);
    }
  }, [platform, onStatusChange]);
  
  const loadPages = async () => {
    setLoadingPages(true);
    try {
      // Use the correct endpoint for listing pages
      const resp = await apiClient.get('/social/facebook/pages');
      if (resp.data.success && resp.data.pages) {
        setPages(resp.data.pages);
      }
    } catch (error) {
      console.error('[MetaAppConfig] Load pages error:', error);
      // Try alternate endpoint
      try {
        const altResp = await apiClient.get('/integrations/facebook/pages');
        if (altResp.data.success && altResp.data.pages) {
          setPages(altResp.data.pages);
        }
      } catch (e) {
        console.log('[MetaAppConfig] No pages available');
      }
    } finally {
      setLoadingPages(false);
    }
  };
  
  // Initial load
  useEffect(() => {
    loadStatus();
  }, [loadStatus]);
  
  // ============================================================================
  // STATE COMPUTATION
  // ============================================================================
  
  const isMetaConfigured = (cfg) => {
    if (!cfg) return false;
    // Custom app with credentials OR default app available
    return (cfg.use_custom_app && cfg.has_app_id && cfg.has_app_secret) || 
           (!cfg.use_custom_app && cfg.has_default_app);
  };
  
  const computeMetaState = (cfg, sts, plat) => {
    // Step 1: Meta App configured?
    if (!isMetaConfigured(cfg)) {
      return META_STATES.META_APP_NOT_CONFIGURED;
    }
    
    // For Instagram, check the detailed status from backend
    if (plat === 'instagram') {
      // P1 FIX: Use detailed Instagram status fields
      const hasOAuth = sts?.user_token_ok || sts?.oauth_connected || sts?.has_user_token;
      const hasPage = sts?.selected_page_id;
      const hasIgBusiness = sts?.selected_ig_business_id || sts?.ig_username;
      const isReady = sts?.publish_ready;
      
      console.info('[MetaAppConfig] Instagram state computation:', {
        hasOAuth, hasPage, hasIgBusiness, isReady,
        blocking_reason: sts?.blocking_reason
      });
      
      if (isReady) {
        return META_STATES.READY_TO_PUBLISH;
      }
      if (hasIgBusiness && hasPage) {
        return META_STATES.OAUTH_CONNECTED; // Almost ready
      }
      if (hasOAuth) {
        return META_STATES.OAUTH_CONNECTED; // OAuth OK, need page/IG link
      }
      return META_STATES.META_APP_CONFIGURED; // Ready for Instagram OAuth
    }
    
    // Step 2: OAuth connected? (for Facebook)
    if (!sts?.has_user_token && !sts?.has_page_token) {
      return META_STATES.OAUTH_NOT_CONNECTED;
    }
    
    // Step 3: Pages available?
    const pagesCount = sts?.available_pages_count || 0;
    if (pagesCount === 0 && sts?.has_user_token) {
      return META_STATES.NO_PAGE_FOUND;
    }
    
    // Step 4: Page selected?
    if (!sts?.selected_page_id && !sts?.page_id) {
      return META_STATES.PAGE_SELECTION_REQUIRED;
    }
    
    // Step 5: Page token present?
    if (!sts?.has_page_token) {
      return META_STATES.PAGE_TOKEN_MISSING;
    }
    
    // Step 6: Can publish?
    if (!sts?.can_publish) {
      return META_STATES.PAGE_TOKEN_MISSING;
    }
    
    return META_STATES.READY_TO_PUBLISH;
  };
  
  // ============================================================================
  // HANDLERS
  // ============================================================================
  
  const handleSaveConfig = async () => {
    // Validation
    if (useCustomApp) {
      // If editing existing config, appId might be masked - check if it's a new value
      const isNewAppId = appId && !appId.includes('****');
      const isNewSecret = appSecret && appSecret.length > 0;
      
      if (!isNewAppId && !config?.has_app_id) {
        toast.error('Le Facebook App ID est requis');
        return;
      }
      
      if (!isNewSecret && !hasExistingSecret) {
        toast.error('Le Facebook App Secret est requis');
        return;
      }
    }
    
    setSaving(true);
    try {
      // SECURITY: Only send new values, never echo back masked values
      const payload = {
        use_custom_app: useCustomApp
      };
      
      if (useCustomApp) {
        // Only send app_id if it's a new value (not masked)
        if (appId && !appId.includes('****')) {
          payload.app_id = appId;
        }
        // Only send secret if provided (new value)
        if (appSecret) {
          payload.app_secret = appSecret;
        }
      }
      
      const response = await apiClient.post('/social/facebook/config', payload);
      
      if (response.data.success) {
        toast.success('Configuration Meta sauvegardée !');
        setAppSecret(''); // SECURITY: Clear secret from UI after save
        setShowSecret(false);
        await loadStatus(); // Reload status
        onConfigSaved?.();
      } else {
        throw new Error(response.data.message || 'Erreur');
      }
    } catch (error) {
      console.error('[MetaAppConfig] Save error:', error);
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };
  
  const handleOAuthConnect = async () => {
    setConnecting(true);
    try {
      // P1 FIX: Use correct endpoint based on platform
      // Instagram and Facebook both use Meta OAuth but different endpoints
      const endpoint = platform === 'instagram' 
        ? '/auth/instagram/connect' 
        : '/auth/facebook/connect';
      
      console.info(`[MetaAppConfig] Starting OAuth for ${platform} via ${endpoint}`);
      const response = await apiClient.get(endpoint);
      
      // P1 FIX: Backend returns "auth_url", not "oauth_url"
      if (response.data.auth_url) {
        toast.info('Redirection vers Meta pour autorisation...');
        window.location.href = response.data.auth_url;
      } else {
        console.error('[MetaAppConfig] No auth_url in response:', response.data);
        throw new Error('URL OAuth non reçue - vérifiez la configuration Meta App');
      }
    } catch (error) {
      console.error('[MetaAppConfig] OAuth error:', error);
      const detail = error.response?.data?.detail || error.response?.data?.message;
      
      // Handle structured error response
      let errorMsg = 'Erreur lors de la connexion OAuth';
      if (typeof detail === 'object' && detail.message) {
        errorMsg = detail.message;
      } else if (typeof detail === 'string') {
        if (detail.includes('CONFIGURE_APP') || detail.includes('app_id') || detail.includes('NO_APP_CONFIGURED')) {
          errorMsg = 'Configuration Meta requise. Renseignez App ID et App Secret.';
          setMetaState(META_STATES.META_APP_NOT_CONFIGURED);
        } else {
          errorMsg = detail;
        }
      }
      
      toast.error(errorMsg);
    } finally {
      setConnecting(false);
    }
  };
  
  const handleSelectPage = async (pageId) => {
    setSelectingPage(true);
    try {
      const response = await apiClient.post('/social/facebook/select-page', {
        page_id: pageId
      });
      
      if (response.data.success) {
        toast.success(`Page "${response.data.page_name}" sélectionnée !`);
        setSelectedPageId(response.data.page_id);
        setSelectedPageName(response.data.page_name);
        await loadStatus(); // Refresh status
      } else {
        throw new Error(response.data.error || 'Erreur');
      }
    } catch (error) {
      console.error('[MetaAppConfig] Select page error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la sélection');
    } finally {
      setSelectingPage(false);
    }
  };
  
  // ============================================================================
  // UI HELPERS
  // ============================================================================
  
  const isConfigured = isMetaConfigured(config);
  const canStartOAuth = isConfigured && metaState !== META_STATES.READY_TO_PUBLISH;
  const showPageSelector = metaState === META_STATES.PAGE_SELECTION_REQUIRED || 
                          metaState === META_STATES.NO_PAGE_FOUND ||
                          (status?.has_user_token && !status?.selected_page_id);
  
  // State message for banner
  const getStateMessage = () => {
    switch (metaState) {
      case META_STATES.META_APP_NOT_CONFIGURED:
        return { 
          type: 'warning', 
          title: 'Configuration requise', 
          message: 'Renseignez votre Facebook App ID et App Secret ci-dessous.' 
        };
      case META_STATES.OAUTH_NOT_CONNECTED:
        return { 
          type: 'info', 
          title: 'Connexion OAuth requise', 
          message: 'Cliquez sur "Connecter via OAuth" pour autoriser l\'accès.' 
        };
      case META_STATES.NO_PAGE_FOUND:
        return { 
          type: 'warning', 
          title: 'Aucune Page trouvée', 
          message: 'Vérifiez que vous êtes administrateur d\'une Page Facebook.' 
        };
      case META_STATES.PAGE_SELECTION_REQUIRED:
        return { 
          type: 'info', 
          title: 'Sélection de Page requise', 
          message: 'Choisissez la Page Facebook sur laquelle publier.' 
        };
      case META_STATES.PAGE_TOKEN_MISSING:
        return { 
          type: 'error', 
          title: 'Token Page manquant', 
          message: 'Reconnectez Facebook pour obtenir un nouveau token.' 
        };
      case META_STATES.READY_TO_PUBLISH:
        return { 
          type: 'success', 
          title: platform === 'facebook' ? 'Facebook prêt !' : 'Config Meta prête', 
          message: platform === 'facebook' 
            ? `Publication autorisée sur "${selectedPageName}"` 
            : 'Vous pouvez maintenant connecter Instagram.'
        };
      default:
        return null;
    }
  };
  
  const stateMessage = getStateMessage();
  
  // ============================================================================
  // RENDER
  // ============================================================================
  
  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-semantic-muted" />
        <span className="ml-2 text-sm text-semantic-muted">Chargement...</span>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid={`meta-config-${platform}`}>
      
      {/* ================================================================
          BLOC 1: Statut de connexion avec états clairs
          ================================================================ */}
      <div className="p-4 rounded-xl bg-semantic-surface-secondary border border-semantic-border">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-medium text-semantic flex items-center gap-2">
            <Info className="w-4 h-4" />
            Statut {platform === 'facebook' ? 'Facebook Pages' : 'Configuration Meta'}
          </h4>
          <button 
            onClick={loadStatus}
            disabled={loading}
            className="p-1.5 rounded-lg hover:bg-semantic-surface-3 transition-colors"
            title="Actualiser"
          >
            <RefreshCw className={cn("w-4 h-4 text-semantic-muted", loading && "animate-spin")} />
          </button>
        </div>
        
        {/* Status Grid */}
        <div className="space-y-1 mb-4">
          <StatusRow 
            label="App Meta" 
            value={isConfigured ? 'Configurée' : 'Non configurée'} 
            status={isConfigured ? 'ok' : 'warning'}
            icon={Key}
          />
          
          {platform === 'facebook' && (
            <>
              <StatusRow 
                label="OAuth" 
                value={status?.has_user_token || status?.has_page_token ? 'Connecté' : 'Non connecté'} 
                status={status?.has_user_token || status?.has_page_token ? 'ok' : 'warning'}
                icon={Globe}
              />
              <StatusRow 
                label="Pages détectées" 
                value={String(status?.available_pages_count || pages.length || (selectedPageId ? 1 : 0))} 
                status={(status?.available_pages_count || pages.length || selectedPageId) ? 'ok' : 'warning'}
                icon={Users}
              />
              <StatusRow 
                label="Page sélectionnée" 
                value={selectedPageName || 'Aucune'} 
                status={selectedPageId ? 'ok' : 'warning'}
                icon={Globe}
              />
              <StatusRow 
                label="Publication" 
                value={status?.can_publish ? 'Autorisée' : 'Non autorisée'} 
                status={status?.can_publish ? 'ok' : 'error'}
                icon={CheckCircle2}
              />
            </>
          )}
          
          {platform === 'instagram' && (
            <>
              <StatusRow 
                label="OAuth Meta" 
                value={status?.user_token_ok || status?.oauth_connected ? 'Connecté' : 'Non connecté'} 
                status={status?.user_token_ok || status?.oauth_connected ? 'ok' : 'warning'}
                icon={Globe}
              />
              <StatusRow 
                label="Page Facebook liée" 
                value={status?.selected_page_id ? (status?.selected_page_name || 'Oui') : 'Non'} 
                status={status?.selected_page_id ? 'ok' : 'warning'}
                icon={Users}
              />
              <StatusRow 
                label="Compte IG Business" 
                value={status?.ig_username || (status?.selected_ig_business_id ? 'Détecté' : 'Non détecté')} 
                status={status?.selected_ig_business_id || status?.ig_username ? 'ok' : 'error'}
                icon={CheckCircle2}
              />
              <StatusRow 
                label="Prêt à publier" 
                value={status?.publish_ready ? 'Oui' : 'Non'} 
                status={status?.publish_ready ? 'ok' : 'error'}
                icon={CheckCircle2}
              />
              {status?.blocking_reason && status?.blocking_reason !== 'POST_READY' && (
                <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs text-amber-700 font-medium">
                    Blocage : {status?.blocking_reason}
                  </p>
                  {status?.blocking_message && (
                    <p className="text-xs text-amber-600 mt-1">{status?.blocking_message}</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        
        {/* State Banner */}
        {stateMessage && (
          <div className={cn(
            'p-3 rounded-lg flex items-start gap-3',
            stateMessage.type === 'warning' && 'bg-amber-500/10 border border-amber-500/20',
            stateMessage.type === 'info' && 'bg-blue-500/10 border border-blue-500/20',
            stateMessage.type === 'error' && 'bg-red-500/10 border border-red-500/20',
            stateMessage.type === 'success' && 'bg-green-500/10 border border-green-500/20'
          )}>
            {stateMessage.type === 'success' ? (
              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            ) : stateMessage.type === 'error' ? (
              <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className={cn(
                'w-5 h-5 flex-shrink-0 mt-0.5',
                stateMessage.type === 'warning' && 'text-amber-600',
                stateMessage.type === 'info' && 'text-blue-600'
              )} />
            )}
            <div>
              <p className={cn(
                'font-medium text-sm',
                stateMessage.type === 'warning' && 'text-amber-700',
                stateMessage.type === 'info' && 'text-blue-700',
                stateMessage.type === 'error' && 'text-red-700',
                stateMessage.type === 'success' && 'text-green-700'
              )}>
                {stateMessage.title}
              </p>
              <p className="text-xs text-semantic-muted mt-1">
                {stateMessage.message}
              </p>
            </div>
          </div>
        )}
      </div>
      
      {/* ================================================================
          BLOC 2: Configuration Meta App (App ID + Secret)
          ================================================================ */}
      <div className="p-4 rounded-xl bg-semantic-surface-secondary border border-semantic-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="font-medium text-semantic flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Configuration App Meta
            </h4>
            <p className="text-xs text-semantic-muted mt-1">
              Partagée entre Facebook Pages et Instagram Business
            </p>
          </div>
          <button
            type="button"
            onClick={() => setUseCustomApp(!useCustomApp)}
            className={cn(
              'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
              useCustomApp ? 'bg-blue-600' : 'bg-gray-300'
            )}
            data-testid="use-custom-meta-app-toggle"
          >
            <span className={cn(
              'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
              useCustomApp ? 'translate-x-6' : 'translate-x-1'
            )} />
          </button>
        </div>
        
        {useCustomApp ? (
          <div className="space-y-4">
            {/* Config status indicator */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-semantic-muted">Statut:</span>
              <StatusBadge 
                status={isConfigured ? 'ok' : 'warning'} 
                label={isConfigured ? 'Configurée' : 'Non configurée'} 
              />
              {config?.app_config_source && (
                <span className="text-xs text-semantic-muted">
                  (Source: {config.app_config_source})
                </span>
              )}
            </div>
            
            {/* App ID Input */}
            <div>
              <label className="block text-sm font-medium text-semantic mb-1.5">
                Facebook App ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={appId}
                onChange={(e) => setAppId(e.target.value)}
                placeholder="Ex: 123456789012345"
                className="w-full input-semantic"
                data-testid="meta-app-id-input"
              />
              {config?.has_app_id && appId?.includes('****') && (
                <p className="text-xs text-green-600 mt-1">
                  ✓ App ID enregistré. Entrez une nouvelle valeur pour remplacer.
                </p>
              )}
            </div>
            
            {/* App Secret Input - SECURITY: Never show existing value */}
            <div>
              <label className="block text-sm font-medium text-semantic mb-1.5">
                Facebook App Secret <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showSecret ? 'text' : 'password'}
                  value={appSecret}
                  onChange={(e) => setAppSecret(e.target.value)}
                  placeholder={hasExistingSecret ? 'Secret enregistré (laissez vide pour conserver)' : 'Entrez votre App Secret'}
                  className="w-full input-semantic pr-10"
                  data-testid="meta-app-secret-input"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowSecret(!showSecret)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-semantic-surface-3 rounded"
                  title={showSecret ? 'Masquer' : 'Afficher'}
                >
                  {showSecret ? (
                    <EyeOff className="w-4 h-4 text-semantic-muted" />
                  ) : (
                    <Eye className="w-4 h-4 text-semantic-muted" />
                  )}
                </button>
              </div>
              {hasExistingSecret && !appSecret && (
                <p className="text-xs text-green-600 mt-1">
                  ✓ Secret déjà enregistré. Laissez vide pour conserver.
                </p>
              )}
              <p className="text-xs text-semantic-muted mt-1">
                Le secret n'est jamais affiché après sauvegarde pour des raisons de sécurité.
              </p>
            </div>
            
            {/* Save Button */}
            <PillButton
              variant="primary"
              onClick={handleSaveConfig}
              disabled={saving}
              icon={saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              className="w-full"
              data-testid="save-meta-config-btn"
            >
              {saving ? 'Sauvegarde...' : 'Sauvegarder la configuration'}
            </PillButton>
            
            {/* Developer Portal Link */}
            <a 
              href="https://developers.facebook.com/apps" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 text-sm text-blue-600 hover:underline"
            >
              <ExternalLink className="w-4 h-4" />
              Ouvrir Meta Developer Portal
            </a>
          </div>
        ) : (
          <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
            <p className="text-sm text-blue-700">
              {config?.has_default_app ? (
                <>✓ Application Meta par défaut disponible.</>
              ) : (
                <>Activez cette option pour utiliser votre propre application Meta Developer.</>
              )}
            </p>
            <p className="text-xs text-semantic-muted mt-2">
              Cette configuration sera partagée entre Facebook Pages et Instagram Business.
            </p>
          </div>
        )}
      </div>
      
      {/* ================================================================
          BLOC 3: Sélection de Page Facebook (si nécessaire)
          ================================================================ */}
      {platform === 'facebook' && showPageSelector && (
        <div className="p-4 rounded-xl bg-semantic-surface-secondary border border-semantic-border">
          <PageSelector
            pages={pages}
            selectedPageId={selectedPageId}
            onSelect={handleSelectPage}
            loading={loadingPages || selectingPage}
            onRefresh={loadPages}
          />
          
          {selectingPage && (
            <div className="mt-3 flex items-center gap-2 text-sm text-semantic-muted">
              <Loader2 className="w-4 h-4 animate-spin" />
              Sélection en cours...
            </div>
          )}
        </div>
      )}
      
      {/* ================================================================
          BLOC 4: Connexion OAuth
          ================================================================ */}
      {showOAuthButton && (
        <div className="p-4 rounded-xl bg-semantic-surface-secondary border border-semantic-border">
          <h4 className="font-medium text-semantic mb-4 flex items-center gap-2">
            Connexion OAuth {platform === 'facebook' ? 'Facebook' : 'Meta'}
          </h4>
          
          {!isConfigured && (
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 mb-4">
              <p className="text-sm text-amber-700 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Configurez d'abord votre App Meta (App ID + Secret) ci-dessus.
              </p>
            </div>
          )}
          
          <PillButton
            variant="primary"
            onClick={handleOAuthConnect}
            disabled={!canStartOAuth || connecting}
            icon={connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
            className="w-full"
            data-testid="meta-oauth-connect-btn"
          >
            {connecting ? 'Connexion...' : oauthButtonLabel}
          </PillButton>
          
          {platform === 'facebook' && (
            <p className="text-xs text-semantic-muted text-center mt-3">
              Après connexion, vous pourrez sélectionner la Page sur laquelle publier.
            </p>
          )}
          
          {platform === 'instagram' && isConfigured && (
            <p className="text-xs text-semantic-muted text-center mt-3">
              La configuration Meta est prête. Connectez-vous à Instagram.
            </p>
          )}
        </div>
      )}
      
      {/* ================================================================
          BLOC 5: Informations App Review
          ================================================================ */}
      <div className="p-4 rounded-xl bg-blue-500/5 border border-blue-500/10">
        <h4 className="font-medium text-blue-700 mb-2 flex items-center gap-2">
          <Info className="w-4 h-4" />
          Permissions requises (App Review)
        </h4>
        <ul className="text-xs text-semantic-muted space-y-1">
          <li>• <strong>pages_manage_posts</strong> - Publier sur vos Pages Facebook</li>
          <li>• <strong>pages_read_engagement</strong> - Lire les statistiques</li>
          <li>• <strong>pages_show_list</strong> - Lister vos Pages</li>
          {(platform === 'instagram' || platform === 'facebook') && (
            <>
              <li>• <strong>instagram_basic</strong> - Accès au compte Instagram Business</li>
              <li>• <strong>instagram_content_publish</strong> - Publier sur Instagram</li>
            </>
          )}
        </ul>
        <p className="text-xs text-semantic-muted mt-3 pt-3 border-t border-blue-500/10">
          💡 Ces permissions doivent être demandées via App Review sur Meta Developer Portal 
          pour les applications en mode Live.
        </p>
      </div>
    </div>
  );
}
